/**
 * Locating a review finding's quoted passage inside the text a reader actually sees.
 *
 * A finding's `quote` is a substring of the article's MARKDOWN SOURCE: it keeps the link syntax,
 * bold markers and table pipes the source carries. What the reader sees (the rendered article and
 * the ProseMirror document) has none of that. Matching the raw quote against rendered text found
 * nothing for any quote containing `[text](url)`, which on production articles was about one
 * finding in five, and "Fix with AI" then did nothing visible.
 *
 * The lookup is shared by the rich editor (ProseMirror positions) and by the review highlights
 * (DOM text offsets), so both agree on which passage a finding points at.
 */

export interface QuoteRange {
  from: number;
  to: number;
}

export type QuoteLookup =
  { status: 'found'; range: QuoteRange } | { status: 'missing' } | { status: 'ambiguous' };

/**
 * A searchable text: one character per entry, `positions[i]` the caller's coordinate for it, and
 * `null` for a block boundary (a character that exists only to keep a quote from bridging two
 * paragraphs that happen to touch).
 */
export interface SearchableText {
  chars: string;
  positions: Array<number | null>;
}

const FOLD: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '“': '"',
  '”': '"',
  ' ': ' ',
};

const BOUNDARY = '\n';

/** Collapses whitespace runs and folds typographic quotes. Boundaries stay boundaries. */
function normalise(text: SearchableText): SearchableText {
  const chars: string[] = [];
  const positions: Array<number | null> = [];
  const last = () => chars[chars.length - 1];
  const dropTrailingSpace = () => {
    if (last() === ' ') {
      chars.pop();
      positions.pop();
    }
  };
  for (let index = 0; index < text.chars.length; index += 1) {
    const position = text.positions[index] ?? null;
    if (position === null) {
      dropTrailingSpace();
      if (chars.length && last() !== BOUNDARY) {
        chars.push(BOUNDARY);
        positions.push(null);
      }
      continue;
    }
    const raw = text.chars[index];
    const char = FOLD[raw] ?? raw;
    if (/\s/.test(char)) {
      if (chars.length && last() !== ' ' && last() !== BOUNDARY) {
        chars.push(' ');
        positions.push(position);
      }
      continue;
    }
    chars.push(char);
    positions.push(position);
  }
  dropTrailingSpace();
  while (last() === BOUNDARY) {
    chars.pop();
    positions.pop();
  }
  return { chars: chars.join(''), positions };
}

/** A needle string as a SearchableText: its own newlines are block boundaries. */
function needleText(needle: string): SearchableText {
  const positions: Array<number | null> = [];
  for (let index = 0; index < needle.length; index += 1)
    positions.push(needle[index] === '\n' ? null : index);
  return { chars: needle, positions };
}

function search(haystack: SearchableText, needle: string): QuoteLookup {
  if (!needle) return { status: 'missing' };
  const start = haystack.chars.indexOf(needle);
  if (start < 0) return { status: 'missing' };
  if (haystack.chars.indexOf(needle, start + 1) >= 0) return { status: 'ambiguous' };
  const from = haystack.positions[start];
  const final = haystack.positions[start + needle.length - 1];
  if (from === null || final === null || from === undefined || final === undefined)
    return { status: 'missing' };
  return { status: 'found', range: { from, to: final + 1 } };
}

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_RULE = /^\s*\|?(?:\s*:?-{2,}:?\s*\|)+\s*:?-*:?\s*\|?\s*$/;
const BLOCK_START = /^\s{0,3}(?:#{1,6}\s+|>\s?|[-*+]\s+|\d{1,3}[.)]\s+)/;
const LINK_URL = String.raw`(?:[^()\s]|\([^()]*\))*(?:\s+"[^"]*")?`;

function stripInlineMarkdown(line: string): string {
  return (
    line
      .replace(new RegExp(String.raw`!\[([^\]]*)\]\(${LINK_URL}\)`, 'g'), '$1')
      .replace(new RegExp(String.raw`\[([^\]]*)\]\(${LINK_URL}\)`, 'g'), '$1')
      // A quote can end inside a link's URL, or begin inside its text.
      .replace(/\[([^\]]+)\]\([^)]*$/, '$1')
      .replace(/^([^[\]]*)\]\([^)]*\)/, '$1')
      .replace(/<(https?:\/\/[^>\s]+)>/g, '$1')
      .replace(/(\*\*|__)(?=\S)(.+?)(?<=\S)\1/g, '$2')
      .replace(/(^|[^*\w])\*(?=\S)([^*]+?)(?<=\S)\*(?!\*)/g, '$1$2')
      .replace(/(^|[^\w])_(?=\S)([^_]+?)(?<=\S)_(?!\w)/g, '$1$2')
      .replace(/~~(?=\S)(.+?)(?<=\S)~~/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\\([\\`*_{}[\]()#+\-.!|>~])/g, '$1')
  );
}

/**
 * The text a reader would see for a passage written as Markdown source. Block structure survives
 * as newlines (headings, list items, table cells and blank-line paragraph breaks), soft line
 * wraps inside a paragraph become spaces.
 */
export function plainTextFromMarkdownQuote(quote: string): string {
  const blocks: string[] = [];
  let current = '';
  const flush = () => {
    if (current.trim()) blocks.push(current.trim());
    current = '';
  };
  for (const line of quote.replace(/\r\n?/g, '\n').split('\n')) {
    if (!line.trim()) {
      flush();
      continue;
    }
    if (TABLE_ROW.test(line) || line.includes('|')) {
      if (TABLE_RULE.test(line)) continue;
      flush();
      for (const cell of line.split(/(?<!\\)\|/)) {
        const text = stripInlineMarkdown(cell.trim());
        if (text) blocks.push(text);
      }
      continue;
    }
    if (BLOCK_START.test(line)) {
      flush();
      current = stripInlineMarkdown(line.replace(BLOCK_START, ''));
      continue;
    }
    const text = stripInlineMarkdown(line.trim());
    current = current ? `${current} ${text}` : text;
  }
  flush();
  return blocks.join('\n');
}

/**
 * Finds the one place a quote occurs. Tries the quote as written, then with whitespace and
 * typographic quotes normalised, then as the plain text its Markdown renders to. The first
 * strategy that finds it exactly once wins; one that finds it twice stops the search, because
 * pointing at the wrong occurrence is worse than pointing at none.
 */
export function lookupQuote(haystack: SearchableText, quote: string): QuoteLookup {
  const exact = search(haystack, quote);
  if (exact.status !== 'missing') return exact;

  const normalHaystack = normalise(haystack);
  for (const candidate of [quote, plainTextFromMarkdownQuote(quote)]) {
    const needle = normalise(needleText(candidate)).chars;
    const result = search(normalHaystack, needle);
    if (result.status !== 'missing') return result;
  }
  return { status: 'missing' };
}
